/**
 * Ticket Lifecycle v2 — Slice 5A.6-lite UI
 *
 * Read-only hook fetching the per-job fields that the lifecycle helpers
 * (`classifyTicketJobMix`, `computeRemainingApprovedWorkMinutes`) need
 * to surface Advisor Delay Impact on `TicketLifecycleTimeCard`.
 *
 * The existing `ticketJobs` prop on `TicketDetailsPopup` exposes only
 * `{ id, name, standardMinutes, category }` — too thin for the helpers,
 * which need `job_status`, `predicted_minutes`, `minutes_override`,
 * `actual_minutes`, plus the catalog `jobs.submenu` and
 * `jobs.proficiency_category` signals used by
 * `isDiagnosticOrInspectionJob`.
 *
 * Scoping mirrors slice-5A.2 `useTicketCustodyEvents`:
 *   - single-ticket scope
 *   - dedicated query key (no cache thrash with broader job queries)
 *   - flag-gated via the `enabled` argument so the lifecycle card can
 *     refuse to fetch when `custodyHandoff` is OFF
 *
 * Boundary: imports only the supabase client. No scheduling / timer /
 * parking / rollover / bay / invariants code.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface TicketJobLifecycleRow {
  job_status: string | null;
  predicted_minutes: number | null;
  minutes_override: number | null;
  actual_minutes: number | null;
  jobs: {
    name: string | null;
    category: string | null;
    submenu: string | null;
    proficiency_category: string | null;
    standard_minutes: number | null;
  } | null;
}

export function useTicketJobsForLifecycle(
  ticketId: string | null | undefined,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ['ticket-jobs-for-lifecycle', ticketId],
    enabled: enabled && !!ticketId,
    retry: false,
    queryFn: async (): Promise<TicketJobLifecycleRow[]> => {
      const { data, error } = await (supabase as any)
        .from('ticket_jobs')
        .select(
          'job_status, predicted_minutes, minutes_override, actual_minutes, jobs:job_id(name, category, submenu, proficiency_category, standard_minutes)',
        )
        .eq('ticket_id', ticketId)
        .is('deleted_at', null);
      if (error) {
        console.error(
          '[ticket-jobs-for-lifecycle] failed to read ticket_jobs',
          error,
        );
        throw error;
      }
      return (data ?? []) as TicketJobLifecycleRow[];
    },
  });
}
